import React, { useContext, useEffect } from "react";
import {
  Box,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  SvgIcon,
} from "@mui/material";

import { primary } from "../../style/color";

import { ReactComponent as DashboardIcon } from "./../../assets/Icons/Dashboard.svg";
import { ReactComponent as CashIcon } from "./../../assets/Icons/Cash.svg";
import { ReactComponent as InvestmentsIcon } from "./../../assets/Icons/fluctuations.svg";
import { ReactComponent as GrantVestmentsIcon } from "./../../assets/Icons/Gantvestments.svg";
import { ReactComponent as GrantIcon } from "./../../assets/Icons/Grant.svg";
import { ReactComponent as SettingsIcon } from "./../../assets/Icons/settings.svg";
import { ReactComponent as ChevronLeftIcon } from "./../../assets/Icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "./../../assets/Icons/chevron-right.svg";
import { ReactComponent as ChevronDownIcon } from "./../../assets/Icons/chevron-down.svg";

import { AuthContext } from "providers/userProvider";
import { Account } from "types/account";
import { golbalTheme } from "style/theme";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FundDashboardPage,
  FundSettingsPage,
  MyCashPageUrl,
  MyGrantVestmentRequestPage,
  MyGrantsRequestsPagePath,
  MyInvestmentsRequestsPage,
} from "constants/pages";
import { AuthData } from "types/auth";
import { FundAccountMember } from "types/fund-account-member";

interface SidebarProps {
  route: string;
}

const truncateText = (text: string, maxLength: number = 25): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};
const cssTooltipStyles = {
  position: 'relative',
  '&:hover .custom-tooltip': {
    visibility: 'visible',
    opacity: 1,
  },
  '.custom-tooltip': {
    visibility: 'hidden',
    opacity: 0,
    position: 'absolute',
    top: '100%', // Position below the element
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    zIndex: 9999,
    transition: 'opacity 0.2s',
    marginTop: '5px',
    '&::before': {
      content: '""',
      position: 'absolute',
      bottom: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      borderWidth: '5px',
      borderStyle: 'solid',
      borderColor: 'transparent transparent rgba(0, 0, 0, 0.8) transparent',
    }
  }
};
export const Sidebar: React.FC<SidebarProps> = ({ route }) => {
  const menuItems = [
    {
      title: "Dashboard",
      icon: <DashboardIcon />,
      page: FundDashboardPage,
      active: "/fund-account",
    },

    {
      title: "My GrantVestments",
      icon: <GrantVestmentsIcon />,
      page: MyGrantVestmentRequestPage,
      active: MyGrantVestmentRequestPage,
    },
    {
      title: "My Investments",
      icon: <InvestmentsIcon />,
      page: MyInvestmentsRequestsPage,
      active: MyInvestmentsRequestsPage,
    },
    {
      title: "My Cash",
      icon: <CashIcon />,
      page: MyCashPageUrl,
      active: MyCashPageUrl,
    },
    {
      title: "My Grants",
      page: MyGrantsRequestsPagePath,
      active: MyGrantsRequestsPagePath,
      icon: <GrantIcon />,
    },
  ];
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, selectedAccount, setSelectedAccount, setUserRole } =
    useContext<AuthData>(AuthContext);
  const [fundAccountMembers, setFundAccounts] = React.useState<
    FundAccountMember[]
  >([]);
  const [account, setAccount] = React.useState<number>(-1);
  const [name, setName] = React.useState<string>("");
  const [checked, setChecked] = React.useState(localStorage.getItem('checked') === 'true');
  // Fixed width constants
  const EXPANDED_WIDTH = 280;
  const COLLAPSED_WIDTH = 85;

  useEffect(() => {
    if (!userData?.user) {
      return;
    }
    const members = userData.user.fundAccounts ?? [];
    setFundAccounts(members);
    if (members.length === 0) {
      setAccount(-1);
      setName("");
      return;
    }
    if (selectedAccount?.fundAccountId != null) {
      setAccount(selectedAccount.fundAccountId);
      setName(getName(selectedAccount));
    }
  }, [userData?.user, selectedAccount]);

  useEffect(() => {
    const checked = localStorage.getItem('checked')
    if (checked) {
      setChecked(checked === 'true')
    }
  }, []);

  const handleChange = (event: any) => {
    event.preventDefault();
    setChecked((prev) => {
      const newValue = !prev;
      localStorage.setItem('checked', (newValue) ? 'true' : 'false')
      return newValue;
    });
  };

  const handleAccountChange = (event: SelectChangeEvent<number>) => {
    event.preventDefault();
    const accountMembers = fundAccountMembers.find(
      (value) => value.fund.fundAccountId === event.target.value
    );
    localStorage.setItem(
      "selectedAccount",
      accountMembers?.fund.fundAccountId?.toString()!
    );
    setSelectedAccount(accountMembers?.fund!);
    setUserRole(accountMembers?.role!);
  };

  const getName = (selectedAccount: Account) => {
    if (selectedAccount) {
      return (selectedAccount as Account)?.fundAccountName
        ?.split(" ")
        .reduce((accumulator, char) => accumulator + char[0], "");
    }
    return "";
  };

  const isActive = (value: string) => {
    return location.pathname.includes(value);
  };

  const isDisabled = () => {
    return location.pathname.includes("add-money");
  };

  const goTo = (link: string) => {
    if (link) {
      navigate(link);
    }
  };



  return (
    <Box>
      {route === "authenticated" && (
        <Collapse
          className="sidebar"
          sx={{
            flex: 1,
            pr: checked ? 3 : 0,
            pt: 2,
            maxWidth: checked ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
            minWidth: checked ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
            width: checked ? EXPANDED_WIDTH : COLLAPSED_WIDTH, // Fixed width
            transition: 'width 0.3s ease, max-width 0.3s ease, min-width 0.3s ease', // Smooth transition
            "& .MuiCollapse-wrapperInner ": {
              width: checked ? EXPANDED_WIDTH : COLLAPSED_WIDTH, // Ensure inner wrapper has fixed width
            },
          }}
          orientation="horizontal"
          in={checked}
          collapsedSize={COLLAPSED_WIDTH}
        >
          <Box sx={{ overflow: "auto", width: '100%' }}>
            <List>
              <ListItem disablePadding>
                <ListItemButton>
                  {checked ? (
                    <Box sx={{ ...cssTooltipStyles, width: '100%' }}>
                      {fundAccountMembers.length > 0 ? (
                        <FormControl size="small" sx={{ width: '100%', maxWidth: 200 }}>
                          <Select
                            disabled={isDisabled()}
                            name="funds"
                            id="funds"
                            value={account}
                            IconComponent={(props) => (
                              <SvgIcon {...props}>
                                <ChevronDownIcon />
                              </SvgIcon>
                            )}
                            sx={{
                              ...golbalTheme.typography.bodyCopyBold,
                              color: "white",
                              backgroundColor: primary[500],
                              "& .Mui-disabled": {
                                backgroundColor: primary[500],
                              },
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "white",
                              },
                              "& .MuiSelect-select": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                width: "100%",
                              },
                            }}
                            onChange={handleAccountChange}
                            readOnly={fundAccountMembers.length === 1}
                            renderValue={(value) => {
                              const account = fundAccountMembers.find(
                                (member) => member.fund.fundAccountId === value
                              );
                              const displayText = truncateText(account?.fund.fundAccountName || "");
                              return (
                                <>
                                  {displayText}
                                  <Box className="custom-tooltip">
                                    {account?.fund.fundAccountName}
                                  </Box>
                                </>
                              );
                            }}
                          >
                            {fundAccountMembers.map((account, index) => (
                              <MenuItem key={index} value={account.fund.fundAccountId}>
                                {account.fund.fundAccountName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <OutlinedInput
                          size="small"
                          readOnly
                          fullWidth
                          value="No DAFs"
                          inputProps={{ style: { textAlign: "center" } }}
                          sx={{
                            ...golbalTheme.typography.bodyCopyBold,
                            color: "white",
                            backgroundColor: primary[500],
                            "& fieldset": { border: "0px" },
                          }}
                        />
                      )}
                    </Box>

                  ) : (
                    <OutlinedInput
                      size="small"
                      onClick={(event) => handleChange(event)}
                      readOnly
                      fullWidth
                      name="funds"
                      id="funds"
                      value={name}
                      inputProps={{ style: { textAlign: "center" } }}
                      sx={{
                        ...golbalTheme.typography.bodyCopyBold,
                        color: "white",
                        backgroundColor: primary[500],
                        "& fieldset": {
                          border: "0px",
                        },
                        "&:hover fieldset": {
                          border: "0px",
                        },
                        "&.Mui-focused fieldset": {
                          border: "0px",
                        },
                        width: '100%',
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
              {menuItems.map((item, index) => {
                return (
                  <ListItem
                    className={isActive(item?.active!) ? "active" : ""}
                    key={index}
                    sx={{ my: 1 }}
                    onClick={() => goTo(item?.page!)}
                    disablePadding
                  >
                    <ListItemButton sx={{ justifyContent: "center" }}>
                      <ListItemIcon
                        sx={{ minWidth: checked ? "56px" : "auto" }}
                      >
                        <SvgIcon>{item.icon}</SvgIcon>
                      </ListItemIcon>
                      {checked && (
                        <ListItemText
                          sx={{
                            m: 0,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: "1",
                            WebkitBoxOrient: "vertical",
                            whiteSpace: "pre-wrap",
                          }}
                          primary={item.title}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
            <Divider variant="middle" sx={{ backgroundColor: primary[100] }} />
            <List>
              <ListItem
                onClick={() => navigate(FundSettingsPage)}
                className={isActive("fund-settings") ? "active" : ""}
                sx={{ my: 1 }}
                disablePadding
              >
                <ListItemButton sx={{ justifyContent: "center" }}>
                  <ListItemIcon sx={{ minWidth: checked ? "56px" : "auto" }}>
                    <SvgIcon>
                      <SettingsIcon />
                    </SvgIcon>
                  </ListItemIcon>
                  {checked && (
                    <ListItemText
                      sx={{
                        m: 0,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: "1",
                        WebkitBoxOrient: "vertical",
                        whiteSpace: "pre-wrap",
                      }}
                      primary={"Fund settings "}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            </List>
            <IconButton
              sx={{ float: "inline-end", m: 1 }}
              onClick={(event) => handleChange(event)}
            >
              <SvgIcon>
                {checked ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </SvgIcon>
            </IconButton>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};
